/**
 * Concrete interpretation copy per digit (1–9) for premium report blocks.
 * Masters (11, 22, 33): same base digit theme + intensity note.
 */

function baseOf(v) {
  const n = Number(v);
  if (n === 11) return 2;
  if (n === 22) return 4;
  if (n === 33) return 6;
  return n;
}

const PACK = {
  1: {
    word: 'initiative',
    core:
      'You are oriented toward starting things, naming truth early, and carrying responsibility for direction — even when the path is still unclear.',
    showsUp: [
      'You step in when nobody else will.',
      'You prefer clarity to endless negotiation.',
      'You can feel alone in the lead even when people are watching.',
    ],
    watchFor: ['Hurry disguised as courage', 'Difficulty receiving help', 'Confusing control with care'],
    useWell: ['Name what you need before you rescue', 'Let others own their part', 'Rest is part of leadership'],
  },
  2: {
    word: 'attunement',
    core:
      'You read rooms, timing, and emotional weather quickly. Cooperation and nuance matter as much as the “right answer.”',
    showsUp: [
      'You adjust before others notice friction.',
      'You remember small signals that shift trust.',
      'Peace can feel like your job, even when it is shared work.',
    ],
    watchFor: ['Swallowed needs', 'Over-adapting to keep calm', 'Resentment from invisible labor'],
    useWell: ['State limits early in plain language', 'Ask for reciprocity without apology', 'Let some tension be informative'],
  },
  3: {
    word: 'expression',
    core:
      'You think and feel through language, connection, and creative shape. Life has to be spoken, shared, or made visible to feel real.',
    showsUp: [
      'You lighten rooms with humor or story.',
      'Ideas move fast from mind to mouth.',
      'Silence can feel heavier than conflict.',
    ],
    watchFor: ['Scatter without follow-through', 'Performing to stay safe', 'Anxiety when you are not “on”'],
    useWell: ['Choose one thread to finish', 'Share drafts before they are perfect', 'Protect quiet as creative fuel'],
  },
  4: {
    word: 'structure',
    core:
      'You build stamina through rhythm, systems, and follow-through. Reliability is a value, not just a habit.',
    showsUp: [
      'You fix what wobbles before it breaks.',
      'You respect time and commitments.',
      'You can carry the invisible load of “making it hold.”',
    ],
    watchFor: ['Rigidity when plans flex', 'Confusing safety with stagnation', 'Grinding without upgrade'],
    useWell: ['Upgrade systems instead of only enduring them', 'Say when a rule no longer serves', 'Let play be scheduled too'],
  },
  5: {
    word: 'movement',
    core:
      'You learn through experience, variety, and honest contact with change. Stillness without meaning can feel like a trap.',
    showsUp: [
      'You test limits to know what is real.',
      'You need freshness to stay engaged.',
      'You sense options before others are ready.',
    ],
    watchFor: ['Restlessness as avoidance', 'Chasing novelty to skip depth', 'Intensity mistaken for intimacy'],
    useWell: ['Name the question you are avoiding', 'Pair freedom with one anchor', 'Let depth be an experiment too'],
  },
  6: {
    word: 'care',
    core:
      'You are wired toward responsibility, repair, and emotional investment in what matters. Harmony is not passive — you work for it.',
    showsUp: [
      'You notice what is missing for others.',
      'You step in as stabilizer when things fray.',
      'You feel weight when people you love are off-center.',
    ],
    watchFor: ['Overgiving', 'Guilt-based yeses', 'Carrying what belongs to someone else'],
    useWell: ['Choose where your care actually belongs', 'Build boundaries before resentment', 'Let others own their growth'],
  },
  7: {
    word: 'depth',
    core:
      'You process through reflection, discernment, and inner truth that cannot be rushed. Surface answers rarely satisfy you.',
    showsUp: [
      'You pause before you commit.',
      'You prefer honest doubt to polite fiction.',
      'You need solitude to hear yourself.',
    ],
    watchFor: ['Isolation as default', 'Skepticism without warmth', 'Analysis that delays contact'],
    useWell: ['Share one real sentence sooner', 'Let trust build in small steps', 'Invite people into your process'],
  },
  8: {
    word: 'impact',
    core:
      'You understand consequence, resources, and power in the material world. You are learning integrity under pressure.',
    showsUp: [
      'You step up when stakes are real.',
      'You track what works and what costs.',
      'You feel responsible for outcomes that touch others.',
    ],
    watchFor: ['Control when scared', 'Confusing worth with winning', 'Collapse of rhythm under load'],
    useWell: ['Name limits on your bandwidth', 'Let influence include rest', 'Measure integrity, not only results'],
  },
  9: {
    word: 'perspective',
    core:
      'You widen the frame: completion, compassion, and letting old chapters end so new ones can breathe.',
    showsUp: [
      'You see patterns across time.',
      'You forgive slowly but deeply.',
      'You feel the weight of what is unfinished.',
    ],
    watchFor: ['Carrying everyone’s pain', 'Difficulty saying “enough”', 'Staying in stories that already closed'],
    useWell: ['Release what you cannot repair', 'Choose a smaller circle to love well', 'Let closure be kindness'],
  },
};

export function packForDigit(v) {
  const b = baseOf(v);
  return PACK[b] || PACK[1];
}

/** Short theme word for a core number (1–9 or master). */
export function digitWord(value) {
  return packForDigit(value).word;
}

export function masterRider(value) {
  const n = Number(value);
  if (n === 11) return 'Master 11: sensitivity and intuition run hot — the same theme as 2, with finer nerves and higher stakes for honesty.';
  if (n === 22) return 'Master 22: building and vision scale up — the same theme as 4, with pressure to make something durable from inspiration.';
  if (n === 33) return 'Master 33: compassion and teaching widen — the same theme as 6, with a stronger call to hold space without losing yourself.';
  return null;
}

/** Plain interpretation body for one digit (no position label). */
export function buildDigitBlockCopy(value, isMaster) {
  const p = packForDigit(value);
  const rider = isMaster ? masterRider(value) : '';
  return {
    coreMeaning: rider ? `${p.core} ${rider}` : p.core,
    showsUp: [...p.showsUp],
    watchFor: [...p.watchFor],
    useWell: [...p.useWell],
  };
}

export function familyPlainName(famKey, familyLabels) {
  return familyLabels[famKey] || 'your leading themes';
}
